import React, { useEffect, useState, useRef } from 'react';
import { Container, Row, Form, Card } from 'react-bootstrap';
import stickybits from 'stickybits';
import Cookies from 'js-cookie';

import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { appsApi, webhooksApi } from '../../api';
import { hasPermissions } from '../../utils';

import PageTitle from '../../components/common/PageTitle';
import Loader from '../../components/common/Loader';
import EndpointForm from '../../components/webhooks/EndpointForm';

const defaultApp = parseInt(Cookies.get('sila_endpoints_app'), 10);

function WebhookEndpoint({ t, user, setAlert }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { id, app, action } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [apps, setApps] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [redirect, setRedirect] = useState(false);
  const [initialValues, setInitialValues] = useState({
    active: false,
    name: '',
    url: '',
    max_attempts: 5,
    retry_interval: 60,
    app_id: app !== 'new' ? app : defaultApp || '',
    app_nickname: '',
    triggers: [],
    alerts_email: '',
    webhook_signing_key: '',
    uuid: '',
  });
  const [active, setActive] = useState(initialValues.active);
  const [name, setName] = useState(initialValues.name);
  const formRef = useRef(null);

  const getEndpoint = async () => {
    try {
      const [endpointResponse, alertsResponse] = await Promise.all([
        webhooksApi.getEndpoint(id, initialValues.app_id),
        webhooksApi.getWebhookAlerts(id),
      ]);
      if (endpointResponse.success && alertsResponse.success) {
        setInitialValues({
          ...endpointResponse.endpoint,
          alerts_email: alertsResponse.webhook_endpoint_alerts.length
            ? alertsResponse.webhook_endpoint_alerts[0].email
            : '',
        });
        setName(endpointResponse.endpoint.name);
        setAlerts(alertsResponse.webhook_endpoint_alerts);
      } else {
        setRedirect(true);
      }
    } catch (error) {
      setAlert({ message: error, variant: 'warning' });
      setRedirect(true);
    } finally {
      setIsLoading(false);
    }
  };

  const regenerateEndpointSigningKey = async () => {
    try {
      const response = await webhooksApi.regenerateEndpointSigningKey(
        initialValues.id,
        initialValues.app_id
      );
      setAlert({ message: response.message, variant: 'success' });
      setInitialValues({ ...initialValues, webhook_signing_key: response.webhook_signing_key });
    } catch (error) {
      setAlert({ message: error, variant: 'warning' });
    }
  };

  useEffect(() => {
    setActive(initialValues.active);
    setName(initialValues.name);
  }, [initialValues]);

  useEffect(() => {
    const fetchPayloads = async () => {
      try {
        const response = await webhooksApi.fetchPayloads(
          triggers.filter((trigger) => !trigger.payload)
        );
        setTriggers(
          triggers.map((trigger) => ({
            ...trigger,
            payload: JSON.stringify(
              response.find((res) => res.name === trigger.name).payload,
              null,
              '\t'
            ),
          }))
        );
      } catch (error) {
        setAlert({ message: error, variant: 'warning' });
        setRedirect(true);
      }
    };
    if (triggers.length && triggers.some((trigger) => !trigger.payload)) fetchPayloads();
  }, [triggers]);

  useEffect(() => {
    const fetchAppsAndTriggers = async () => {
      try {
        const [appsResponse, triggersResponse] = await Promise.all([
          appsApi.fetchApps({ team: user.currentTeam.id, limit: 20 }),
          webhooksApi.getTriggers(),
        ]);
        if (appsResponse.success && appsResponse.apps.length && triggersResponse.success) {
          setApps(appsResponse.apps.map((a) => ({ name: a.name, id: a.id })));
          setTriggers(triggersResponse.triggers);
        } else {
          setRedirect(true);
        }
      } catch (error) {
        setAlert({ message: error, variant: 'warning' });
        setRedirect(true);
      } finally {
        if (action === 'detail') {
          getEndpoint();
        } else {
          setIsLoading(false);
        }
      }
    };
    fetchAppsAndTriggers();
  }, [user.currentTeam]);

  useEffect(() => {
    if (redirect) navigate({ pathname: '/dev/webhooks', state: { from: location.pathname } });
  }, [redirect]);

  useEffect(() => {
    stickybits('.triggers .trigger-search', { useStickyClasses: true });
  }, []);

  return (
    <Container className="webhooks main-content-container p-3 p-md-5 d-flex flex-column flex-grow-1">
      <Row className="g-0 page-header mb-3 mb-md-5 loaded">
        <PageTitle
          title={t(
            `webhooks.endpoints.headers.${location.pathname.includes('new') ? 'create' : 'detail'}`
          )}
          backBtn
        />
      </Row>

      {isLoading ? (
        <Loader />
      ) : (
        <Card className="loaded">
          {action === 'detail' && (
            <Card.Header className="d-flex justify-content-between p-4">
              <h3 className="m-0">{name}</h3>
              {hasPermissions(user, 'webhooks', 'Write') ? (
                <Form>
                  <Form.Check
                    type="switch"
                    id="endpoint-active"
                    checked={active}
                    onChange={(e) => {
                      const { checked } = e.target;
                      setActive(checked);
                      formRef.current.setFieldValue('active', checked);
                    }}
                    label={t('common:status.active')}
                  />
                </Form>
              ) : (
                <p className={`text-lg text-${initialValues.active ? 'success' : 'warning'}`}>
                  {t(`common:status.${initialValues.active ? 'active' : 'inactive'}`)}
                </p>
              )}
            </Card.Header>
          )}
          <Card.Body className="p-4">
            <EndpointForm
              formRef={formRef}
              initialValues={initialValues}
              setName={setName}
              triggers={triggers}
              apps={apps}
              alerts={alerts}
              getEndpoint={getEndpoint}
              regenerateEndpointSigningKey={regenerateEndpointSigningKey}
            />
          </Card.Body>
        </Card>
      )}
    </Container>
  );
}

export default WebhookEndpoint;
